import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function ROICalculator() {
  const [initialInvestment, setInitialInvestment] = useState("");
  const [finalValue, setFinalValue] = useState("");
  const [result, setResult] = useState<{ roi: number; gain: number } | null>(null);

  const calculate = () => {
    const initial = parseFloat(initialInvestment);
    const final = parseFloat(finalValue);

    if (isNaN(initial) || isNaN(final) || initial <= 0) {
      toast.error("Please enter valid positive values");
      return;
    }

    const gain = final - initial;
    const roi = (gain / initial) * 100;

    setResult({ roi, gain });
    toast.success("ROI calculated!");
  };

  const reset = () => {
    setInitialInvestment("");
    setFinalValue("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="ROI Calculator" calculatorPath="/calculator/roi">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">ROI Calculator</h1>
          <p className="text-muted-foreground">
            Calculate Return on Investment to measure profitability
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: ROI = [(Final Value - Initial Investment) / Initial Investment] × 100</CardTitle>
            <CardDescription>
              ROI measures the efficiency of an investment as a percentage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="initialInvestment">Initial Investment ($)</Label>
                <Input
                  id="initialInvestment"
                  type="number"
                  placeholder="10000"
                  value={initialInvestment}
                  onChange={(e) => setInitialInvestment(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="finalValue">Final Value ($)</Label>
                <Input
                  id="finalValue"
                  type="number"
                  placeholder="12000"
                  value={finalValue}
                  onChange={(e) => setFinalValue(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculate} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">Reset</Button>
            </div>

            {result && (
              <>
                <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 border-emerald-500/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Results</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-lg break-all overflow-wrap-anywhere">
                        <strong>ROI:</strong> {result.roi.toFixed(2)}%
                      </p>
                      <CopyResultButton value={result.roi.toFixed(2)} />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-lg break-all overflow-wrap-anywhere">
                        <strong>Net Gain/Loss:</strong> ${result.gain.toFixed(2)}
                      </p>
                      <CopyResultButton value={result.gain.toFixed(2)} />
                    </div>
                  </CardContent>
                </Card>

                <CalculatorSuperCalcCTA config={calculatorCTAs["roi"]} trigger={result?.roi} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter your initial investment amount</p>
                <p>2. Enter the final value (current or sold value)</p>
                <p>3. Click Calculate to see ROI percentage and net gain/loss</p>
                <p>4. Positive ROI = profit, Negative ROI = loss</p>
              </CardContent>
            </Card>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Example</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p><strong>Problem:</strong> You invested $10,000 and it's now worth $12,000.</p>
                <p><strong>Solution:</strong></p>
                <div className="pl-4 space-y-1">
                  <p>Net Gain = $12,000 - $10,000 = $2,000</p>
                  <p>ROI = ($2,000 / $10,000) × 100</p>
                  <p>ROI = 0.2 × 100 = 20%</p>
                  <p>You gained 20% return on your investment</p>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
